import axios, { AxiosError } from 'axios';
import { ApiError } from './ApiService';

const GEO_PLACES_URL = 'https://orbiterapicaching.azurewebsites.net/api/GeoPlacesSearch';
const GEO_PLACES_CODE = 'wb56QYsk8ES97cKRjjCMFNzO8JBITlQC_rNrwVE0pdTkAzFuaOk06w==';

export interface IGeoPlaceSuggestion {
  type: string;
  displayName: string;
  countryCode?: string;
}

const toApiError = (e: unknown): unknown => {
  if (e instanceof AxiosError) {
    const status = e.response?.status;
    const responseData = e.response?.data;
    const baseMessage = (e.response?.data?.Message as string | undefined) || e.message;
    const message = status ? `${baseMessage} (${status})` : baseMessage;
    return new ApiError(message, status, responseData, e);
  }
  return e;
};

/**
 * Lightweight client for calling third-party APIs that are not part of our backend.
 * Centralizes external requests to keep components clean and testable.
 */
export class ExternalApiService {
  /**
   * Suggest places (cities, regions, countries, postcodes) while the member types.
   * Suggestions have no coordinates; resolve the chosen one with `resolveGeoPlace`.
   * @param query The partial place name typed so far
   */
  static async suggestGeoPlaces(
    query: string,
    signal?: AbortSignal
  ): Promise<IGeoPlaceSuggestion[]> {
    const q = (query || '').trim();
    if (!q) return [];

    try {
      const resp = await axios.get(GEO_PLACES_URL, {
        params: { code: GEO_PLACES_CODE, mode: 'suggest', query: q },
        timeout: 10000,
        signal,
      });
      return (resp.data?.results ?? []) as IGeoPlaceSuggestion[];
    } catch (e) {
      throw toApiError(e);
    }
  }

  /**
   * Get the coordinates of a place suggestion the member picked.
   * @param suggestion A suggestion returned by `suggestGeoPlaces`
   */
  static async resolveGeoPlace(
    suggestion: IGeoPlaceSuggestion,
    signal?: AbortSignal
  ): Promise<{ latitude: number; longitude: number }> {
    try {
      const resp = await axios.get(GEO_PLACES_URL, {
        params: {
          code: GEO_PLACES_CODE,
          mode: 'resolve',
          place: suggestion.displayName,
          countryCode: suggestion.countryCode ?? '',
          type: suggestion.type,
        },
        timeout: 10000,
        signal,
      });
      return { latitude: resp.data.latitude, longitude: resp.data.longitude };
    } catch (e) {
      throw toApiError(e);
    }
  }
}

export default ExternalApiService;
